import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableCell,
  TableRow,
  TextField,
  TextareaAutosize,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
  tableCellClasses,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postDataCatalogForm } from "../../../redux/features/data-catalog/data-catalog-slice";
import { updateDataCatalogById } from "../../../redux/features/data-catalog/data-catalog-slice";
import { NewDataCatalog, SpecificRules } from "../../../types/businessSettings";
import { NewDataCatalogs } from "../../../constants/parent-settings";
import { DataCatalogConcepts } from "../../../constants/catalog";
import DeleteIcon from "@mui/icons-material/Delete";
import "./business-settings.scss";
import { InsuranceProvider } from "../../../types/response";
import { RootState } from "../../../redux/store";
import { AuthCode } from "../../../types/client";
import { GetAuthcode } from "../../../redux/features/client-main/client-insurance-slice";
import DataCatalogService from "../../../service/dataCatalog.service";
import GooglePlacesAutocomplete, {
  geocodeByAddress,
} from "react-google-places-autocomplete";
import { getAddressObject } from "src/utils/AddressUtils";
import ServicePlaceService from "../../../service/servicePlace.service";

const ITEM_HEIGHT = 36; // Reduced from 48
const ITEM_PADDING_TOP = 4; // Reduced from 8

// eslint-disable-next-line
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  MenuListProps: {
    style: {
      padding: '4px 0',
    },
  },
};

// Custom styles for Select items
const selectSx = {
  '& .MuiSelect-select': {
    padding: '8px 14px',
    minHeight: '20px !important',
    lineHeight: '1.2',
  },
  '& .MuiMenuItem-root': {
    minHeight: '32px',
  },
};

// Custom Table
const StyledTable = styled(Table)(({ theme }) => ({
  [`& .${tableCellClasses.root}`]: {
    borderBottom: "none",
  },
}));

// Custom Table Col style
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  padding: "5px 20px",
}));

// Custom Table Row style
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#ECF6FF",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
    height: "30px",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  height: "40px",
}));

interface IdataCatalog {
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedCatalogData: React.Dispatch<React.SetStateAction<NewDataCatalog>>;
  isEdit: boolean;
  selectedCatalogData: NewDataCatalog;
  filtercatalogname: string;
}

const CatalogTemplate: React.FC<IdataCatalog> = ({
  setOpenDialog,
  setSelectedCatalogData,
  isEdit,
  selectedCatalogData,
  filtercatalogname,
}) => {
  //#region variable region

  //store value
  const [value, setValue] = useState<string>(
    isEdit ? selectedCatalogData.conceptValue : ""
  );

  // Insurance tab state
  const [insuranceTab, setInsuranceTab] = useState<
    "Basic" | "PlaceOfService" | "Modifiers"
  >("Basic");

  // Insurance specific data
  const [insuranceData, setInsuranceData] = useState<{
    insuranceName: string;
    payerId: string;
    network: "in" | "out" | "both" | "";
    city: string;
    state: string;
    zipCode: string;
    fullAddress: string;
    placeOfService: string[];
    modifiers: string[];
  }>({
    insuranceName: "",
    payerId: "",
    network: "",
    city: "",
    state: "",
    zipCode: "",
    fullAddress: "",
    placeOfService: [],
    modifiers: [],
  });

  // Service places and modifiers for dropdown options
  const [servicePlaces, setServicePlaces] = useState<
    { code: string; name: string }[]
  >([]);
  const [modifiersList, setModifiersList] = useState<
    { id: string; conceptValue: string }[]
  >([]);

  //stores insurance values
  const insuranceProviders = useSelector<RootState, InsuranceProvider[]>(
    (state) => state.clientInsuranceSlice.clientInsurance
  );

  //stores auth codes
  const authCodes = useSelector<RootState, AuthCode[]>(
    (state) => state.clientInsuranceSlice.clientAuthcode
  );

  //address
  const [address, setAddress] = useState<string>(
    selectedCatalogData.data ? selectedCatalogData.data.fullAddress : ""
  );

  //store selected category
  const [category, setCategory] = useState<string>(
    isEdit
      ? selectedCatalogData.catalogName
      : filtercatalogname
      ? filtercatalogname
      : "Auth Code"
  );

  //stores selected insurance min per day and unit per day
  const [authSubData, setAuthSubData] = useState<{
    minsPerUnit: number;
    unitsPerDay: number;
    rules: SpecificRules[];
  }>({
    minsPerUnit: 15,
    unitsPerDay: 0,
    rules: [],
  });

  //store message description
  const [description, setDescription] = useState<string>(
    isEdit ? selectedCatalogData.description : ""
  );

  //handle Error
  const [error, setError] = useState<boolean>(false);

  //used to dispatch
  const dispatch = useDispatch();

  //#endregion

  //#region methods region

  // Handle tab change
  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: "Basic" | "PlaceOfService" | "Modifiers"
  ) => {
    setInsuranceTab(newValue);
  };

  // Handle address selection from Google Places (with auto-populate)
  const handleAddress = async (selected: any) => {
    const description: string | undefined = selected?.label;
    if (!description) return;

    setAddress(description.split(",")[0]);

    // Set full address (only street portion before first comma like Biz Unit)
    setInsuranceData((prev) => ({
      ...prev,
      fullAddress: description.split(",")[0],
    }));

    try {
      const results = await geocodeByAddress(description);
      const addressObject: any = getAddressObject(
        results[0].address_components as any
      );

      setInsuranceData((prev) => ({
        ...prev,
        city: addressObject?.city || prev.city,
        state: addressObject?.province || prev.state,
        zipCode: addressObject?.postal_code || prev.zipCode,
      }));
    } catch (e) {
      console.error("Failed to geocode address", e);
    }
  };

  // Handle insurance data changes
  const handleInsuranceDataChange = (field: string, newValue: any) => {
    setInsuranceData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  // Remove a place of service from selection
  const handleRemovePlaceOfService = (codeToRemove: string) => {
    setInsuranceData((prev) => ({
      ...prev,
      placeOfService: prev.placeOfService.filter(
        (code) => code !== codeToRemove
      ),
    }));
  };

  // Remove a modifier from selection
  const handleRemoveModifier = (modifierToRemove: string) => {
    setInsuranceData((prev) => ({
      ...prev,
      modifiers: prev.modifiers.filter((mod) => mod !== modifierToRemove),
    }));
  };

  //handles deletion of chip
  const handleChipDelete = (
    index: number,
    dataindex: number,
    type: "insurance" | "parallelCodes" | "billingCodes"
  ) => {
    const data = authSubData.rules[index][type].filter(
      (_, insuranceIndex) => insuranceIndex !== dataindex
    );
    setAuthSubData((prevState) => ({
      ...prevState,
      rules: prevState.rules.map((rule, ruleIndex) => {
        if (ruleIndex === index) {
          return {
            ...rule,
            [type]: data,
          };
        }
        return rule;
      }),
    }));
  };

  const handleOnChangeRules = (
    value: string[] | string | boolean,
    index: number,
    type:
      | "insurance"
      | "parallelCodes"
      | "noOfClients"
      | "groupCode"
      | "doubleBookings"
      | "billingOverlapAllowed"
      | "billingCodes"
  ) => {
    setAuthSubData((prevState) => ({
      ...prevState,
      rules: prevState.rules.map((rule, ruleIndex) => {
        if (ruleIndex === index) {
          return {
            ...rule,
            [type]: value,
          };
        }
        return rule;
      }),
    }));
  };

  //handle Delete Rule
  const handleDeleteRule = (index: number) => {
    setAuthSubData((prevState) => {
      const ruleArr = prevState.rules.filter((_, i) => i !== index);
      return {
        ...prevState,
        rules: ruleArr,
      };
    });
  };

  //handle save catalog
  const handleSave = () => {
    if (!value.trimStart()) {
      setError(true);
      return;
    }

    if (isEdit) {
      if (category === "Insurance") {
        // Ensure insuranceName matches value for consistency
        const updatedInsuranceData = {
          ...insuranceData,
          insuranceName: value.trimStart(),
        };

        dispatch(
          updateDataCatalogById({
            id: selectedCatalogData.id,
            catalogName: category,
            conceptValue: value,
            isActive: selectedCatalogData.isActive,
            description: description,
            data: updatedInsuranceData,
          })
        );
      } else if (category === "Auth Code" && authSubData) {
        dispatch(
          updateDataCatalogById({
            id: selectedCatalogData.id,
            catalogName: category,
            conceptValue: value,
            isActive: selectedCatalogData.isActive,
            description: description,
            data: authSubData,
          })
        );
      } else {
        dispatch(
          updateDataCatalogById({
            id: selectedCatalogData.id,
            catalogName: category,
            conceptValue: value,
            isActive: selectedCatalogData.isActive,
            description: description,
          })
        );
      }
    } else {
      if (category === "Insurance") {
        // Ensure insuranceName matches value for consistency
        const updatedInsuranceData = {
          ...insuranceData,
          insuranceName: value.trimStart(),
        };

        dispatch(
          postDataCatalogForm({
            catalogName: category,
            conceptValue: value,
            isActive: false,
            description: description,
            data: updatedInsuranceData,
          })
        );
      } else if (category === "Auth Code" && authSubData) {
        dispatch(
          postDataCatalogForm({
            catalogName: category,
            conceptValue: value,
            isActive: false,
            description: description,
            data: authSubData,
          })
        );
      } else {
        dispatch(
          postDataCatalogForm({
            catalogName: category,
            conceptValue: value,
            isActive: false,
            description: description,
          })
        );
      }
    }
    setOpenDialog(false);
    setSelectedCatalogData(NewDataCatalogs);
  };

  //#endregion

  //#region UseEffect region
  // Initialize Auth Code data
  useEffect(() => {
    if (
      isEdit &&
      selectedCatalogData?.data &&
      selectedCatalogData.catalogName === "Auth Code" &&
      selectedCatalogData.data?.rules
    ) {
      setAuthSubData({
        minsPerUnit: selectedCatalogData.data?.minsPerUnit,
        unitsPerDay: selectedCatalogData.data?.unitsPerDay,
        rules: selectedCatalogData.data?.rules,
      });
    }
  }, [isEdit, selectedCatalogData?.data, selectedCatalogData.catalogName]);

  // Initialize Insurance data
  useEffect(() => {
    if (
      isEdit &&
      selectedCatalogData?.data &&
      selectedCatalogData.catalogName === "Insurance"
    ) {
      setInsuranceData({
        insuranceName:
          selectedCatalogData.data.insuranceName ||
          selectedCatalogData.conceptValue,
        payerId: selectedCatalogData.data.payerId || "",
        network: selectedCatalogData.data.network || "",
        city: selectedCatalogData.data.city || "",
        state: selectedCatalogData.data.state || "",
        zipCode: selectedCatalogData.data.zipCode || "",
        fullAddress: selectedCatalogData.data.fullAddress || "",
        placeOfService: selectedCatalogData.data.placeOfService || [],
        modifiers: selectedCatalogData.data.modifiers || [],
      });
    }
  }, [isEdit, selectedCatalogData?.data, selectedCatalogData.catalogName]);

  // Update insuranceName when value changes
  useEffect(() => {
    if (category === "Insurance") {
      setInsuranceData((prev) => ({
        ...prev,
        insuranceName: value,
      }));
    }
  }, [value, category]);

  // Fetch service places and modifiers when category is Insurance
  useEffect(() => {
    if (category === "Insurance") {
      // Fetch service places
      ServicePlaceService.getServicePlaces()
        .then((res: any) => {
          if (res.data) {
            setServicePlaces(res.data);
          }
        })
        .catch((err: any) =>
          console.error("Error fetching service places:", err)
        );

      // Fetch modifiers
      DataCatalogService.GetAllModifiers()
        .then((res) => {
          if (res.data) {
            setModifiersList(res.data);
          }
        })
        .catch((err) => console.error("Error fetching modifiers:", err));
    }
  }, [category]);

  //used to fetch auth codes
  useEffect(() => {
    dispatch(GetAuthcode());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //#endregion

  return (
    <div className="p-40px message-templateBclobk">
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          md={12}
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div className="fromGroup-chips">
            <Typography className="fromLabel" style={{ marginTop: "10px" }}>
              Catalog
            </Typography>
          </div>
          <div className="fromGroup-chips">
            {isEdit ? (
              <Card className="fromGroup-chip-active">
                {selectedCatalogData.catalogName}
              </Card>
            ) : filtercatalogname ? (
              <Card className="fromGroup-chip-active">{filtercatalogname}</Card>
            ) : (
              Object.values(DataCatalogConcepts)
                .filter((item) => item !== "Template Variable")
                .map((item) => (
                  <Card
                    key={item}
                    className={category === item ? "fromGroup-chip-active" : ""}
                    onClick={() => {
                      setCategory(item);
                      setValue("");
                    }}
                  >
                    {item}
                  </Card>
                ))
            )}
          </div>

          {!category && error && (
            <FormHelperText style={{ color: "#d32f2f" }}>
              Please select category
            </FormHelperText>
          )}
        </Grid>

        {/* Insurance Tabs */}
        {(filtercatalogname || selectedCatalogData.catalogName || category) ===
          "Insurance" && (
          <Grid item xs={12} md={12} style={{ marginTop: "20px" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={insuranceTab}
                onChange={handleTabChange}
                aria-label="insurance tabs"
              >
                <Tab label="Basic Info" value="Basic" />
                <Tab label="Places of Service" value="PlaceOfService" />
                <Tab label="Modifiers" value="Modifiers" />
              </Tabs>
            </Box>
          </Grid>
        )}

        {/* Basic Info Tab - Insurance Name, Payer ID, Network, Address */}
        {(filtercatalogname || selectedCatalogData.catalogName || category) ===
          "Insurance" &&
          insuranceTab === "Basic" && (
            <>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Insurance Name
                  </InputLabel>
                  <TextField
                    placeholder="Enter insurance name"
                    value={value.trimStart()}
                    onChange={(e) => {
                      setValue(e.target.value);
                      handleInsuranceDataChange(
                        "insuranceName",
                        e.target.value
                      );
                    }}
                  />
                  {!value.trimStart() && error && (
                    <FormHelperText style={{ color: "#d32f2f" }}>
                      Insurance name cannot be empty
                    </FormHelperText>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Payer ID
                  </InputLabel>
                  <TextField
                    placeholder="Enter payer ID"
                    value={insuranceData.payerId}
                    onChange={(e) =>
                      handleInsuranceDataChange("payerId", e.target.value)
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Network
                  </InputLabel>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={insuranceData.network}
                    onChange={(_, newValue) =>
                      handleInsuranceDataChange("network", newValue ?? "")
                    }
                    sx={{
                      display: "flex",
                      gap: "12px",
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        borderColor: "#c4c4c4",
                        borderRadius: "6px",
                      },
                      "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                        marginLeft: 0,
                      },
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "1px solid #c4c4c4",
                      },
                      "& .Mui-selected": {
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        backgroundColor: "rgba(25,118,210,0.08)",
                      },
                    }}
                  >
                    <ToggleButton value="in">In-Network</ToggleButton>
                    <ToggleButton value="out">Out-of-Network</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Address
                  </InputLabel>
                  <GooglePlacesAutocomplete
                    apiKey={process.env.React_APP_GOOGLE_AUTOCOMPLETE_KEY}
                    selectProps={{
                      defaultInputValue: address.trimStart(),
                      setValue: address.trimStart(),
                      onChange: handleAddress,
                      debounce: 300,
                      styles: {
                        input: (provided: any) => ({
                          ...provided,
                          fontSize: 16,
                        }),
                        option: (provided: any) => ({
                          ...provided,
                          fontSize: 16,
                        }),
                        singleValue: (provided: any) => ({
                          ...provided,
                          fontSize: 16,
                        }),
                      },
                    }}
                    autocompletionRequest={{
                      componentRestrictions: {
                        country: ["us", "ca"],
                      },
                    }}
                    onLoadFailed={(error) =>
                      console.error("Could not inject Google script", error)
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    City
                  </InputLabel>
                  <TextField
                    placeholder="Enter city"
                    value={insuranceData.city}
                    onChange={(e) =>
                      handleInsuranceDataChange("city", e.target.value)
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    State
                  </InputLabel>
                  <TextField
                    placeholder="Enter state"
                    value={insuranceData.state}
                    onChange={(e) =>
                      handleInsuranceDataChange("state", e.target.value)
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    ZipCode
                  </InputLabel>
                  <TextField
                    placeholder="Enter zip code"
                    value={insuranceData.zipCode}
                    onChange={(e) =>
                      handleInsuranceDataChange("zipCode", e.target.value)
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Description (optional)
                  </InputLabel>
                  <TextareaAutosize
                    placeholder="Enter Description "
                    minRows={5}
                    aria-label="minimum height"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{ width: "100%", padding: "10px" }}
                  />
                </Box>
              </Grid>
            </>
          )}

        {/* Places of Service Tab */}
        {(filtercatalogname || selectedCatalogData.catalogName || category) ===
          "Insurance" &&
          insuranceTab === "PlaceOfService" && (
            <>
              <Grid item xs={12} md={6}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Selected Places of Service
                  </InputLabel>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid #e0e0e0",
                      height: 260,
                      overflowY: "auto",
                    }}
                  >
                    {insuranceData.placeOfService.length === 0 ? (
                      <Typography
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 2 }}
                      >
                        No places of service selected
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {insuranceData.placeOfService.map((code) => {
                          const place = servicePlaces.find(
                            (p) => p.code === code
                          );
                          return (
                            <Chip
                              key={code}
                              label={
                                place ? `${place.code} - ${place.name}` : code
                              }
                              onDelete={() => handleRemovePlaceOfService(code)}
                              sx={{ mb: 1 }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Search Places of Service
                  </InputLabel>
                  <Autocomplete
                    multiple
                    id="places-of-service-search"
                    options={servicePlaces}
                    getOptionLabel={(option) =>
                      `${option.code} - ${option.name}`
                    }
                    filterSelectedOptions
                    renderTags={() => null}
                    value={servicePlaces.filter((place) =>
                      insuranceData.placeOfService.includes(place.code)
                    )}
                    onChange={(event, newValue) => {
                      setInsuranceData((prev) => ({
                        ...prev,
                        placeOfService: newValue.map((item) => item.code),
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search for places of service"
                      />
                    )}
                  />
                </Box>
              </Grid>
            </>
          )}

        {/* Modifiers Tab */}
        {(filtercatalogname || selectedCatalogData.catalogName || category) ===
          "Insurance" &&
          insuranceTab === "Modifiers" && (
            <>
              <Grid item xs={12} md={6}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Selected Modifiers
                  </InputLabel>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid #e0e0e0",
                      height: 260,
                      overflowY: "auto",
                    }}
                  >
                    {insuranceData.modifiers.length === 0 ? (
                      <Typography
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 2 }}
                      >
                        No modifiers selected
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {insuranceData.modifiers.map((modifier) => (
                          <Chip
                            key={modifier}
                            label={modifier}
                            onDelete={() => handleRemoveModifier(modifier)}
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Modifiers
                  </InputLabel>
                  <FormControl fullWidth>
                    <Select
                      multiple
                      value={insuranceData.modifiers}
                      onChange={(e) =>
                        handleInsuranceDataChange(
                          "modifiers",
                          e.target.value as string[]
                        )
                      }
                      renderValue={() => ""}
                      MenuProps={MenuProps}
                    >
                      {modifiersList.map((m) => (
                        <MenuItem key={m.id} value={m.conceptValue}>
                          <Checkbox
                            checked={
                              insuranceData.modifiers.indexOf(m.conceptValue) >
                              -1
                            }
                          />
                          <ListItemText primary={m.conceptValue} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </>
          )}

        {/* Auth Code UI */}
        {(filtercatalogname || selectedCatalogData.catalogName || category) ===
          "Auth Code" && (
          <>
            <Grid item xs={12} md={4}>
              <Box className="fromGroup">
                <InputLabel className="fromLabel" style={{ marginTop: "10px" }}>
                  Record Value
                </InputLabel>
                <TextField
                  placeholder="Enter new value"
                  value={value.trimStart()}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                />
                {!value.trimStart() && error && (
                  <FormHelperText style={{ color: "#d32f2f" }}>
                    Item value cannot be empty
                  </FormHelperText>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box className="fromGroup">
                <InputLabel className="fromLabel" style={{ marginTop: "10px" }}>
                  Per unit Type
                </InputLabel>
                <Select
                  labelId="PerUnitType"
                  id="perUnit"
                  value={authSubData.minsPerUnit}
                  onChange={(e) => {
                    setAuthSubData((pre) => {
                      return { ...pre, minsPerUnit: e.target.value as number };
                    });
                  }}
                  MenuProps={MenuProps}
                >
                  <MenuItem value={15}>Per 15 Minutes</MenuItem>
                  <MenuItem value={30}>Per 30 Minutes</MenuItem>
                  <MenuItem value={60}>Per 1 Hour</MenuItem>
                  <MenuItem value={0}>Per Assessment</MenuItem>
                </Select>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box className="fromGroup">
                <InputLabel className="fromLabel" style={{ marginTop: "10px" }}>
                  Units Per Day
                </InputLabel>
                <Select
                  labelId="UnitPerDay"
                  id="unitperday"
                  value={authSubData.unitsPerDay}
                  MenuProps={MenuProps}
                  onChange={(e) => {
                    setAuthSubData((pre) => {
                      return { ...pre, unitsPerDay: e.target.value as number };
                    });
                  }}
                >
                  <MenuItem value={8}>8 Units</MenuItem>
                  <MenuItem value={16}>16 Units</MenuItem>
                  <MenuItem value={18}>18 Units</MenuItem>
                  <MenuItem value={24}>24 Units</MenuItem>
                  <MenuItem value={32}>32 Units</MenuItem>
                </Select>
              </Box>
            </Grid>
          </>
        )}

        {/* Other catalog types */}
        {(filtercatalogname || selectedCatalogData.catalogName || category) !==
          "Auth Code" &&
          (filtercatalogname || selectedCatalogData.catalogName || category) !==
            "Insurance" && (
            <>
              <Grid item xs={12} md={4}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Record Value
                  </InputLabel>
                  <TextField
                    placeholder="Enter new value"
                    value={value.trimStart()}
                    onChange={(e) => {
                      setValue(e.target.value);
                    }}
                  />
                  {!value.trimStart() && error && (
                    <FormHelperText style={{ color: "#d32f2f" }}>
                      Item value cannot be empty
                    </FormHelperText>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box className="fromGroup">
                  <InputLabel
                    className="fromLabel"
                    style={{ marginTop: "10px" }}
                  >
                    Description (optional)
                  </InputLabel>
                  <TextareaAutosize
                    placeholder="Enter Description "
                    minRows={5}
                    aria-label="minimum height"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{ width: "100%", padding: "10px" }}
                  />
                </Box>
              </Grid>
            </>
          )}
      </Grid>

      {/* Auth Code Special Rules */}
      {(filtercatalogname || selectedCatalogData.catalogName || category) ===
        "Auth Code" && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Specific Rules
              </Typography>
              <Button
                color="primary"
                onClick={() => {
                  setAuthSubData((prevState) => {
                    return {
                      ...prevState,
                      rules: [
                        ...prevState.rules,
                        {
                          insurance: [],
                          groupCode: false,
                          noOfClients: 0,
                          doubleBookings: false,
                          parallelCodes: [],
                          billingOverlapAllowed: false,
                          billingCodes: [],
                        },
                      ],
                    };
                  });
                }}
              >
                + Add Rule
              </Button>
            </Box>
          </Grid>

          {authSubData?.rules.map((item, index) => (
            <Grid item xs={12} key={index}>
              <Box
                sx={{
                  backgroundColor: "#ECF6FF",
                  borderRadius: "8px",
                  p: 2,
                  mb: 2,
                  position: "relative",
                }}
              >
                {/* ----------  New two-section layout  ---------- */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "nowrap",
                    overflowX: "auto", // enable horizontal scroll on mobile
                    WebkitOverflowScrolling: "touch", // smooth iOS scrolling
                  }}
                >
                  {/* ----------  LEFT : Insurance (fixed width & scrollable) ---------- */}
                  <Box
                    sx={{
                      flex: "0 0 260px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Insurance
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        multiple
                        value={item.insurance}
                        renderValue={() => ""}
                        MenuProps={MenuProps}
                        sx={selectSx}
                        onChange={(e) =>
                          handleOnChangeRules(
                            e.target.value,
                            index,
                            "insurance"
                          )
                        }
                      >
                        {insuranceProviders.map((unit, ind) => (
                          <MenuItem
                            key={ind}
                            value={unit.insuranceProviderName}
                            sx={{ py: 0.5 }}
                          >
                            <Checkbox
                              checked={
                                item.insurance.indexOf(
                                  unit.insuranceProviderName
                                ) > -1
                              }
                            />
                            <ListItemText
                              primary={unit.insuranceProviderName}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {/* Insurance chips – one per line */}
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {item.insurance.map((value, dataindex) => (
                        <Chip
                          key={dataindex}
                          label={value}
                          onDelete={() =>
                            handleChipDelete(index, dataindex, "insurance")
                          }
                          sx={{ flex: "0 0 100%" }}
                        />
                      ))}
                    </Box>
                  </Box>{" "}
                  {/* end left insurance box */}
                  {/* ----------  RIGHT : existing grid (checkboxes + inputs + delete) ---------- */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "grid",
                      gridTemplateColumns: "4fr 3fr auto",
                      gridTemplateRows: "auto auto auto",
                      columnGap: 2,
                      rowGap: 2,
                    }}
                  >
                    {/* Group Booking checkbox */}
                    <Box
                      sx={{
                        gridColumn: "1",
                        gridRow: "1",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        pt: 0.5,
                        alignItems: "center"
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, whiteSpace: "nowrap", textAlign: "center" }}
                      >
                        Group Booking Allowed?
                      </Typography>
                      <Checkbox
                        color="primary"
                        checked={item.groupCode}
                        onChange={() =>
                          handleOnChangeRules(
                            !item.groupCode,
                            index,
                            "groupCode"
                          )
                        }
                      />
                    </Box>

                    {/* Double Booking checkbox */}
                    <Box
                      sx={{
                        gridColumn: "1",
                        gridRow: "2",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        pt: 0.5,
                        alignItems: "center"
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, whiteSpace: "nowrap", textAlign: "center" }}
                      >
                        Double Booking Allowed?
                      </Typography>
                      <Checkbox
                        color="primary"
                        checked={item.doubleBookings}
                        onChange={() =>
                          handleOnChangeRules(
                            !item.doubleBookings,
                            index,
                            "doubleBookings"
                          )
                        }
                      />
                    </Box>

                    {/* Billing As Zero checkbox */}
                    <Box
                      sx={{
                        gridColumn: "1",
                        gridRow: "3",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        pt: 0.5,
                        alignItems: "center"
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, whiteSpace: "nowrap", textAlign: "center" }}
                      >
                        Billing {value.trim() || "this code"} as zero?
                      </Typography>
                      <Checkbox
                        color="primary"
                        checked={item.billingOverlapAllowed || false}
                        onChange={() =>
                          handleOnChangeRules(
                            !(item.billingOverlapAllowed || false),
                            index,
                            "billingOverlapAllowed"
                          )
                        }
                      />
                    </Box>

                    {/* # of Clients input */}
                    <Box sx={{ gridColumn: "2", gridRow: "1" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1, fontWeight: 500, whiteSpace: "nowrap" }}
                      >
                        # of Clients
                      </Typography>
                      <TextField
                        type="number"
                        fullWidth
                        disabled={!item.groupCode}
                        value={item.noOfClients}
                        onChange={(e) =>
                          handleOnChangeRules(
                            e.target.value,
                            index,
                            "noOfClients"
                          )
                        }
                        InputProps={{ 
                          inputProps: { min: 0 },
                          sx: {
                            padding: '0px',
                            '& input': {
                              padding: '8px 14px',
                              height: '20px',
                            }
                          }
                        }}
                        size="small"
                      />
                    </Box>

                    {/* Overlap With Codes (parallelCodes) */}
                    <Box sx={{ gridColumn: "2", gridRow: "2" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1, fontWeight: 500, whiteSpace: "nowrap" }}
                      >
                        Overlap With Codes
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          disabled={!item.doubleBookings}
                          multiple
                          value={item.parallelCodes || []}
                          renderValue={() => ""}
                          MenuProps={MenuProps}
                          sx={selectSx}
                          onChange={(e) =>
                            handleOnChangeRules(
                              e.target.value as string[],
                              index,
                              "parallelCodes"
                            )
                          }
                        >
                          {authCodes.map((unit, ind) => (
                            <MenuItem key={ind} value={unit.conceptValue} sx={{ py: 0.5 }}>
                              <Checkbox
                                checked={
                                  (item?.parallelCodes || []).indexOf(
                                    unit.conceptValue
                                  ) > -1
                                }
                              />
                              <ListItemText primary={unit.conceptValue} />
                            </MenuItem>
                          ))}
                        </Select>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          {(item.parallelCodes || []).map(
                            (value, dataindex) => (
                              <Chip
                                key={dataindex}
                                label={value}
                                onDelete={() =>
                                  handleChipDelete(
                                    index,
                                    dataindex,
                                    "parallelCodes"
                                  )
                                }
                                sx={{ m: 0.5 }}
                              />
                            )
                          )}
                        </Box>
                      </FormControl>
                    </Box>

                    {/* Overlap with Codes (billingCodes) */}
                    <Box sx={{ gridColumn: "2", gridRow: "3" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1, fontWeight: 500, whiteSpace: "nowrap" }}
                      >
                        Overlap with Codes
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          disabled={!item.billingOverlapAllowed}
                          multiple
                          value={item.billingCodes || []}
                          renderValue={() => ""}
                          MenuProps={MenuProps}
                          sx={selectSx}
                          onChange={(e) =>
                            handleOnChangeRules(
                              e.target.value as string[],
                              index,
                              "billingCodes"
                            )
                          }
                        >
                          {authCodes.map((unit, ind) => (
                            <MenuItem key={ind} value={unit.conceptValue} sx={{ py: 0.5 }}>
                              <Checkbox
                                checked={
                                  (item?.billingCodes || []).indexOf(
                                    unit.conceptValue
                                  ) > -1
                                }
                              />
                              <ListItemText primary={unit.conceptValue} />
                            </MenuItem>
                          ))}
                        </Select>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 1,
                          }}
                        >
                          {(item.billingCodes || []).map((value, dataindex) => (
                            <Chip
                              key={dataindex}
                              label={value}
                              onDelete={() =>
                                handleChipDelete(
                                  index,
                                  dataindex,
                                  "billingCodes"
                                )
                              }
                              sx={{ m: 0.5 }}
                            />
                          ))}
                        </Box>
                      </FormControl>
                    </Box>

                    {/* Delete button */}
                    <Box
                      sx={{
                        gridColumn: "3",
                        gridRow: "1 / span 3",
                        display: "flex",
                        alignItems: "start",
                        justifyContent: "center",
                      }}
                    >
                      <DeleteIcon
                        color="primary"
                        sx={{
                          cursor: "pointer",
                          fontSize: "24px",
                          "&:hover": {
                            color: "error.main",
                          },
                        }}
                        onClick={() => handleDeleteRule(index)}
                      />
                    </Box>
                  </Box>{" "}
                  {/* end right grid */}
                </Box>{" "}
                {/* end two-section flex */}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <Box className="stepperForm__footer b-0">
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          className="button"
          sx={{ mt: 2 }}
        >
          {isEdit ? "Save Changes" : "Save Item"}
        </Button>
      </Box>
    </div>
  );
};

export default CatalogTemplate;
